import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, InputNumber, Popconfirm, Progress, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { COMPETENCY_PERMS as P, useAssessmentCycles, useCompetencyAction, useCompetencyCan } from '@/hooks/useCompetency';
import { competencyService, type AssessmentCycle } from '@/services/competency.service';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import { COMPETENCY_BASE, STATUS_COLOR } from './competencyDisplay';

const { Text } = Typography;

const AssessmentCyclesPage: React.FC = () => {
  const navigate = useNavigate();
  const canClose = useCompetencyCan(P.assessmentClose);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const { data = [], isLoading } = useAssessmentCycles();

  const generate = useCompetencyAction(async (y: number) => {
    const r = await competencyService.generateCycle(y);
    message.info(`${r.created} new draft assessment(s) for ${r.eligible} eligible employee(s)`);
    return r;
  });
  const closeCycle = useCompetencyAction((cycleId: string) => competencyService.closeCycle(cycleId), 'Cycle closed');

  const columns: ColumnsType<AssessmentCycle> = [
    { title: 'Year', dataIndex: 'cycle_year', width: 90 },
    { title: 'Cut-off date', dataIndex: 'cutoff_date', width: 140, render: (v) => formatDate(v) },
    {
      title: 'Progress',
      render: (_, r) => (
        <Space>
          <Progress percent={r.total ? Math.round((Number(r.closed) / Number(r.total)) * 100) : 0} size="small" style={{ width: 200 }} />
          <Text type="secondary">
            {r.closed} of {r.total} closed
          </Text>
        </Space>
      ),
    },
    { title: 'Status', dataIndex: 'status', width: 100, render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
    {
      title: '',
      width: 220,
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => navigate(`${COMPETENCY_BASE}/assessments?cycle_id=${r.id}`)}>
            Assessments
          </Button>
          {canClose && r.status === 'Open' && (
            <Popconfirm title={`Close the ${r.cycle_year} cycle?`} onConfirm={() => closeCycle.mutate(r.id)}>
              <Button size="small">Close</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title="Yearly assessment cycles"
        extra={
          canClose ? (
            <Space>
              <InputNumber min={2000} max={2100} value={year} onChange={(v) => v && setYear(v)} />
              <Popconfirm
                title={`Generate the ${year} cycle?`}
                description="A draft assessment is created for every active employee with at least 12 months of service on 31 December. Existing drafts are kept."
                onConfirm={() => generate.mutate(year)}
              >
                <Button type="primary" loading={generate.isPending}>
                  Generate drafts
                </Button>
              </Popconfirm>
            </Space>
          ) : null
        }
      />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="The yearly cycle is generated automatically in December. Line managers and department heads are notified to complete the drafts for their teams."
      />
      <Card>
        <Table rowKey="id" size="small" loading={isLoading} dataSource={data} columns={columns} pagination={false} />
      </Card>
    </div>
  );
};

export default AssessmentCyclesPage;
